import { supabase } from '@/lib/supabase'
import {
  type CreateConversationBody,
  type PostSparkyMessageResponse,
  SparkyApiError,
  type SparkyAttachment,
  type SparkyConversation,
  SparkyDisabledError,
  type SparkyMessage,
  type SparkyModelKey,
  type SparkyModelOption,
  type SparkySaveResult,
  SparkyRunActiveError,
  type SparkyRunDetail,
} from './types'

/**
 * Sparky copilot API client (spec-29 §7). Mirrors the journey feature client's
 * Supabase-bearer pattern and lives in the feature so it stays mockable in
 * tests (`vi.mock('./api')`). Feature-detects the two backend error codes the
 * UI branches on: 503 `sparky_disabled` (flag off → disabled state) and 409
 * `run_active` (one active run per conversation → locked composer).
 */

const BASE = '/api/v1/sparky'

async function authHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  return headers
}

/** Read the backend's `{"error": ...}` code from a failed response, if any. */
async function errorCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { error?: unknown }
    if (typeof body.error === 'string') return body.error
  } catch {
    // Non-JSON body — no code to extract.
  }
  return null
}

async function fail(response: Response): Promise<never> {
  const code = await errorCode(response)
  if (code === 'sparky_disabled') throw new SparkyDisabledError()
  if (code === 'run_active') throw new SparkyRunActiveError()
  throw new Error(`Verzoek mislukt (${response.status})`)
}

/** Own conversations, most recently updated first. */
export async function getConversations(): Promise<SparkyConversation[]> {
  const response = await fetch(`${BASE}/conversations`, { headers: await authHeaders() })
  if (!response.ok) return fail(response)
  return (await response.json()) as SparkyConversation[]
}

/** Open a new chat thread with a frozen context snapshot (spec §2). */
export async function createConversation(
  body: CreateConversationBody,
): Promise<SparkyConversation> {
  const response = await fetch(`${BASE}/conversations`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as SparkyConversation
}

/** Soft-delete a conversation (it disappears from the sidebar). */
export async function deleteConversation(conversationId: string): Promise<void> {
  const response = await fetch(`${BASE}/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  if (!response.ok) return fail(response)
}

/** Full chat history for one conversation, chronological. */
export async function getMessages(conversationId: string): Promise<SparkyMessage[]> {
  const response = await fetch(`${BASE}/conversations/${conversationId}/messages`, {
    headers: await authHeaders(),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as SparkyMessage[]
}

/**
 * Post one chat turn — 202 + the queued run to poll. A 503 `sparky_disabled`
 * becomes a `SparkyDisabledError`, a 409 `run_active` a `SparkyRunActiveError`.
 */
export async function postMessage(
  conversationId: string,
  content: string,
  attachmentIds: readonly string[] = [],
): Promise<PostSparkyMessageResponse> {
  const response = await fetch(`${BASE}/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(
      attachmentIds.length > 0 ? { content, attachment_ids: attachmentIds } : { content },
    ),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as PostSparkyMessageResponse
}

/** Poll one run's status (`GET .../runs/{run_id}`, 2s cadence — spec §8). */
export async function getRun(conversationId: string, runId: string): Promise<SparkyRunDetail> {
  const response = await fetch(`${BASE}/conversations/${conversationId}/runs/${runId}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as SparkyRunDetail
}

/** Like `fail`, but keeps the API's Dutch `error` text (uploads and saves show it inline). */
async function failWithMessage(response: Response, fallback: string): Promise<never> {
  let message: string | null = null
  let code: string | null = null
  try {
    const body = (await response.json()) as { error?: unknown; code?: unknown }
    if (typeof body.error === 'string') message = body.error
    if (typeof body.code === 'string') code = body.code
  } catch {
    // Non-JSON body: use the fallback text.
  }
  throw new SparkyApiError(message ?? fallback, response.status, code)
}

/** The model options (labels and hints come from the backend). */
export async function getModels(): Promise<SparkyModelOption[]> {
  const response = await fetch(`${BASE}/models`, { headers: await authHeaders() })
  if (!response.ok) return fail(response)
  return (await response.json()) as SparkyModelOption[]
}

/** Switch the model of the next answer. */
export async function patchConversation(
  conversationId: string,
  body: { model_choice: SparkyModelKey },
): Promise<SparkyConversation> {
  const response = await fetch(`${BASE}/conversations/${conversationId}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as SparkyConversation
}

/** Upload one file as a pending attachment (multipart). */
export async function uploadAttachment(
  conversationId: string,
  file: File,
): Promise<SparkyAttachment> {
  const headers = await authHeaders()
  delete headers['Content-Type'] // the browser sets the multipart boundary
  const body = new FormData()
  body.append('file', file)
  const response = await fetch(`${BASE}/conversations/${conversationId}/attachments`, {
    method: 'POST',
    headers,
    body,
  })
  if (!response.ok) return failWithMessage(response, 'Uploaden mislukt')
  return (await response.json()) as SparkyAttachment
}

/** Every live attachment of a conversation (pending ones have `message_id === null`). */
export async function listAttachments(conversationId: string): Promise<SparkyAttachment[]> {
  const response = await fetch(`${BASE}/conversations/${conversationId}/attachments`, {
    headers: await authHeaders(),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as SparkyAttachment[]
}

/** Remove a pending attachment. */
export async function deleteAttachment(
  conversationId: string,
  attachmentId: string,
): Promise<void> {
  const response = await fetch(
    `${BASE}/conversations/${conversationId}/attachments/${attachmentId}`,
    { method: 'DELETE', headers: await authHeaders() },
  )
  if (!response.ok) return failWithMessage(response, 'Verwijderen mislukt')
}

/** File an attachment into a deal's documents. */
export async function saveAttachment(
  attachmentId: string,
  body: { deal_id: string; folder_id: string | null; doc_type: string },
): Promise<SparkySaveResult> {
  const response = await fetch(`${BASE}/attachments/${attachmentId}/save`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  })
  if (!response.ok) return failWithMessage(response, 'Opslaan mislukt')
  return (await response.json()) as SparkySaveResult
}

/** File an answer (as a PDF) into a deal's documents. */
export async function saveMessage(
  messageId: string,
  body: { deal_id: string; folder_id: string | null },
): Promise<SparkySaveResult> {
  const response = await fetch(`${BASE}/messages/${messageId}/save`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  })
  if (!response.ok) return failWithMessage(response, 'Opslaan mislukt')
  return (await response.json()) as SparkySaveResult
}
